import { Controller } from '@hotwired/stimulus'

const FIELD_TYPES = [
  { value: 'single_select', label: 'Выбор одного' },
  { value: 'multi_select', label: 'Выбор нескольких' },
  { value: 'color', label: 'Цвет' },
  { value: 'number', label: 'Число' },
  { value: 'dimensions', label: 'Размеры (Ш×Д)' },
  { value: 'text', label: 'Текст' }
]

const TYPES_WITH_OPTIONS = ['single_select', 'multi_select', 'color']

const TYPE_COLORS = {
  single_select: { border: 'border-l-blue-400', badge: 'bg-blue-100 text-blue-700' },
  multi_select: { border: 'border-l-purple-400', badge: 'bg-purple-100 text-purple-700' },
  color: { border: 'border-l-pink-400', badge: 'bg-pink-100 text-pink-700' },
  number: { border: 'border-l-amber-400', badge: 'bg-amber-100 text-amber-700' },
  dimensions: { border: 'border-l-teal-400', badge: 'bg-teal-100 text-teal-700' },
  text: { border: 'border-l-gray-400', badge: 'bg-gray-200 text-gray-700' }
}

export default class extends Controller {
  static targets = ['fieldsContainer', 'hidden']
  static values = { fields: Array }

  connect() {
    this.fields = this.fieldsValue || []
    this.render()
  }

  addField() {
    this.fields.push({
      key: `field_${Date.now()}`,
      label: '',
      type: 'single_select',
      options: []
    })
    this.render()
  }

  removeField(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.fields.splice(index, 1)
    this.render()
  }

  updateFieldLabel(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.fields[index].label = event.currentTarget.value
    this.fields[index].key = this.generateKey(event.currentTarget.value)
    this.updateHidden()
  }

  updateFieldType(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const newType = event.currentTarget.value
    const field = this.fields[index]

    field.type = newType

    if (TYPES_WITH_OPTIONS.includes(newType)) {
      if (newType === 'color') {
        field.options = (field.options || []).map(opt =>
          typeof opt === 'string' ? { name: opt, hex: '#000000' } : opt
        )
      } else {
        field.options = (field.options || []).map(opt =>
          typeof opt === 'object' ? opt.name : opt
        )
      }
    } else if (newType === 'number') {
      field.min = field.min || 0
      field.max = field.max || 100
      delete field.options
    } else {
      delete field.options
      delete field.min
      delete field.max
    }

    this.render()
  }

  addOption(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const field = this.fields[index]

    if (field.type === 'color') {
      field.options.push({ name: '', hex: '#000000' })
    } else {
      field.options.push('')
    }
    this.render()
  }

  updateOption(event) {
    const { index, optionIndex } = event.currentTarget.dataset
    const field = this.fields[parseInt(index)]

    if (field.type === 'color') {
      field.options[parseInt(optionIndex)].name = event.currentTarget.value
    } else {
      field.options[parseInt(optionIndex)] = event.currentTarget.value
    }
    this.updateHidden()
  }

  updateColorHex(event) {
    const { index, optionIndex } = event.currentTarget.dataset
    this.fields[parseInt(index)].options[parseInt(optionIndex)].hex = event.currentTarget.value
    this.updateHidden()
  }

  removeOption(event) {
    const { index, optionIndex } = event.currentTarget.dataset
    this.fields[parseInt(index)].options.splice(parseInt(optionIndex), 1)
    this.render()
  }

  updateNumberMin(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.fields[index].min = parseInt(event.currentTarget.value) || 0
    this.updateHidden()
  }

  updateNumberMax(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.fields[index].max = parseInt(event.currentTarget.value) || 100
    this.updateHidden()
  }

  render() {
    this.fieldsContainerTarget.innerHTML = this.fields.map((field, index) =>
      this.renderField(field, index)
    ).join('')
    this.updateHidden()
  }

  renderField(field, index) {
    const colors = TYPE_COLORS[field.type] || TYPE_COLORS.text
    const typeLabel = FIELD_TYPES.find(t => t.value === field.type)?.label || field.type
    const typeOptions = FIELD_TYPES.map(t =>
      `<option value="${t.value}" ${t.value === field.type ? 'selected' : ''}>${t.label}</option>`
    ).join('')

    let optionsHtml = ''

    if (TYPES_WITH_OPTIONS.includes(field.type)) {
      const items = (field.options || []).map((opt, optIdx) => {
        if (field.type === 'color') {
          return `
            <div class="flex items-center gap-2 mb-2 bg-white rounded-md p-2 border border-gray-100">
              <input type="color" value="${opt.hex || '#000000'}"
                     data-action="change->field-presets#updateColorHex"
                     data-index="${index}" data-option-index="${optIdx}"
                     class="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer p-0.5">
              <input type="text" value="${this.escapeAttr(opt.name || '')}"
                     placeholder="Название цвета"
                     data-action="input->field-presets#updateOption"
                     data-index="${index}" data-option-index="${optIdx}"
                     class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <button type="button" data-action="click->field-presets#removeOption"
                      data-index="${index}" data-option-index="${optIdx}"
                      class="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md text-lg px-2 py-1 transition-colors">✕</button>
            </div>`
        }
        return `
          <div class="flex items-center gap-2 mb-2 bg-white rounded-md p-2 border border-gray-100">
            <span class="text-xs text-gray-300 w-5 text-center">${optIdx + 1}</span>
            <input type="text" value="${this.escapeAttr(opt)}"
                   placeholder="Значение"
                   data-action="input->field-presets#updateOption"
                   data-index="${index}" data-option-index="${optIdx}"
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <button type="button" data-action="click->field-presets#removeOption"
                    data-index="${index}" data-option-index="${optIdx}"
                    class="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md text-lg px-2 py-1 transition-colors">✕</button>
          </div>`
      }).join('')

      const count = (field.options || []).length
      optionsHtml = `
        <div class="mt-3 pt-3 border-t border-gray-200">
          <label class="block text-xs font-medium text-gray-500 mb-2">Значения ${count > 0 ? '<span class="text-gray-400">(' + count + ')</span>' : ''}</label>
          ${items}
          <button type="button" data-action="click->field-presets#addOption"
                  data-index="${index}"
                  class="mt-1 inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded-md transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
            Добавить значение
          </button>
        </div>`
    } else if (field.type === 'number') {
      optionsHtml = `
        <div class="mt-3 pt-3 border-t border-gray-200">
          <label class="block text-xs font-medium text-gray-500 mb-2">Диапазон</label>
          <div class="flex items-center gap-3">
            <div class="flex-1">
              <label class="block text-xs text-gray-400 mb-1">от</label>
              <input type="number" value="${field.min || 0}"
                     data-action="input->field-presets#updateNumberMin"
                     data-index="${index}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            </div>
            <span class="text-gray-300 mt-5">—</span>
            <div class="flex-1">
              <label class="block text-xs text-gray-400 mb-1">до</label>
              <input type="number" value="${field.max || 100}"
                     data-action="input->field-presets#updateNumberMax"
                     data-index="${index}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            </div>
          </div>
        </div>`
    }

    return `
      <div class="border border-gray-200 border-l-4 ${colors.border} rounded-lg p-4 bg-white shadow-sm">
        <div class="flex items-start justify-between mb-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-semibold text-gray-500">${index + 1}</span>
            <span class="inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}">${typeLabel}</span>
          </div>
          <button type="button" data-action="click->field-presets#removeField"
                  data-index="${index}"
                  class="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md text-lg px-2 py-1 transition-colors leading-none">✕</button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Название поля</label>
            <input type="text" value="${this.escapeAttr(field.label)}"
                   placeholder="Например: Материал"
                   data-action="input->field-presets#updateFieldLabel"
                   data-index="${index}"
                   class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Тип поля</label>
            <select data-action="change->field-presets#updateFieldType"
                    data-index="${index}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              ${typeOptions}
            </select>
          </div>
        </div>
        ${optionsHtml}
      </div>`
  }

  updateHidden() {
    const cleanFields = this.fields
      .filter(f => f.label.trim() !== '')
      .map(f => {
        const clean = { key: f.key, label: f.label, type: f.type }
        if (TYPES_WITH_OPTIONS.includes(f.type)) {
          clean.options = (f.options || []).filter(opt =>
            typeof opt === 'string' ? opt.trim() !== '' : opt.name?.trim() !== ''
          )
        }
        if (f.type === 'number') {
          clean.min = f.min
          clean.max = f.max
        }
        return clean
      })

    this.hiddenTarget.value = JSON.stringify(cleanFields)
  }

  generateKey(label) {
    return label
      .toLowerCase()
      .replace(/[а-яё]/g, char => {
        const map = { 'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
          'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
          'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u', 'ф': 'f', 'х': 'h',
          'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e',
          'ю': 'yu', 'я': 'ya' }
        return map[char] || char
      })
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  }

  escapeAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }
}
